/* eslint-disable consistent-return */
import React from 'react'
import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'

import { CarouselControls } from './CarouselControls'
import BeatList from './BeatList'

import type { MicroWidgetProps } from '..'

import { useCopyStore, useEvent } from '../context/hooks'

import type { Beat } from '../../core/types/Beat'

import { ANALYTICS_CLICK_EVENTS } from '../constants/Analytics'
import { MINIMUM_SWIPE_DISTANCE } from '../constants/App'

interface MicroBeatCarouselProps {
  allowSingleCardPeeking?: boolean
  analyticsMetadata?: Record<string, string>
  beats?: Beat[]
  elevation?: number
  heightOverrides?: MicroWidgetProps['heightOverrides']
  onCardClick?: MicroWidgetProps['onCardClick']
  posthog?: MicroWidgetProps['posthog']
  showCarouselControls?: boolean
  showIcon?: boolean
  showWithMargin?: boolean
  variant?: 'outlined' | 'elevation' | undefined
  visibleCardsCount?: number
  widthOverrides?: number
}

const MicroBeatCarousel: React.FC<MicroBeatCarouselProps> = ({
  analyticsMetadata,
  allowSingleCardPeeking,
  beats = [],
  elevation,
  heightOverrides,
  onCardClick,
  posthog,
  showCarouselControls,
  showIcon,
  showWithMargin = true,
  variant,
  widthOverrides,
  visibleCardsCount = 1, // Default to 1 card visible
}) => {
  // ************** Use Ref Hooks **************************
  const ref = React.useRef<HTMLDivElement | null>(null)
  const carouselRef = React.useRef<HTMLDivElement | null>(null)

  // ************** Use States Hooks ***********************
  const [scrollX, setScrollX] = React.useState<number>(0)
  const [touchEnd, setTouchEnd] = React.useState<number | null>(null)
  const [touchStart, setTouchStart] = React.useState<number | null>(null)
  const [cardWidth, setCardWidth] = React.useState<number | null>(null)

  // *************** Context functions *********************
  const { carouselCopy: copy } = useCopyStore()
  const { onEvent } = useEvent()

  // ************** Use Effects Hooks **********************

  React.useEffect(() => {
    // Single source of truth for card-width sizing. The two layout modes
    // (breakpoint-based peeking vs. proportional visible-cards math) are
    // mutually exclusive on `allowSingleCardPeeking`, so they share one
    // ResizeObserver/effect to keep resize tracking in one place.
    //
    // Observe the outer, structurally stable container (carouselRef) rather
    // than the inner shrink-wrapped flex row (ref). The inner row's width
    // depends on cardWidth, so observing it would feed back into itself and
    // cause a shrinking ResizeObserver loop / stuck-small layout.
    const computeCardWidth = (observedWidth?: number) => {
      const node = carouselRef.current
      if (!node) return

      // Prefer the width reported by the ResizeObserver entry to avoid an
      // extra layout read (thrashing); fall back to a DOM read on mount and
      // on the window-resize fallback path.
      const rawWidth = observedWidth ?? node.offsetWidth

      if (allowSingleCardPeeking) {
        // Use until token sizes are actually implemented in the mxui repo
        const breakpoints = [
          { maxWidth: 320, cardWidth: 220 },
          { maxWidth: 375, cardWidth: 268 },
          { maxWidth: 444, cardWidth: 300 },
          { maxWidth: 599, cardWidth: 350 },
          { maxWidth: 749, cardWidth: 450 },
          { maxWidth: 900, cardWidth: 600 },
        ]

        const matchingBreakpoint = breakpoints.find((bp) => rawWidth <= bp.maxWidth)
        setCardWidth(matchingBreakpoint ? matchingBreakpoint.cardWidth : 750)
        return
      }

      // The inner row is constrained to `calc(100% - 32px)` when showWithMargin
      // is set, so subtract that margin to match the actual available width.
      // Clamp to 0 so a tiny/unmeasured container never yields a negative width.
      const containerWidth = showWithMargin ? Math.max(0, rawWidth - 32) : rawWidth
      const gap = 8 // gap between cards

      // When visibleCardsCount > 1, make each card narrower to fit multiple cards and show a peek of the next card
      // When visibleCardsCount is 1, use full container width
      let calculatedCardWidth
      if (visibleCardsCount > 1) {
        // First calculate what a single full card width would be
        const fullCardWidth = (containerWidth - (visibleCardsCount - 1) * gap) / visibleCardsCount
        // Set the peek area to be 1/3 of a card width instead of fixed 60px
        const peekAreaWidth = fullCardWidth / 3
        // Calculate final card width, accounting for the peek area and gaps
        calculatedCardWidth =
          (containerWidth - peekAreaWidth - (visibleCardsCount - 1) * gap) / visibleCardsCount
      } else {
        calculatedCardWidth = containerWidth
      }

      // Clamp to prevent negative widths.
      setCardWidth(Math.max(0, calculatedCardWidth))
    }

    computeCardWidth()

    // Recompute on container resize via ResizeObserver, falling back to the
    // window resize event only when ResizeObserver is unavailable.
    const handleResize = () => computeCardWidth()
    let observer: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined' && carouselRef.current) {
      observer = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (!entry) return
        const width = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width
        computeCardWidth(width)
      })
      observer.observe(carouselRef.current)
    } else {
      window.addEventListener('resize', handleResize)
    }

    return () => {
      window.removeEventListener('resize', handleResize)
      observer?.disconnect()
    }
  }, [allowSingleCardPeeking, visibleCardsCount, showWithMargin])

  // *************** Click handlers ************************
  const handleNextClick = () => {
    onEvent(ANALYTICS_CLICK_EVENTS.ON_CAROUSEL_FORWARD_CLICK, analyticsMetadata)

    if (ref.current && cardWidth) {
      // Scroll by one card width + gap
      ref.current.scrollTo({
        left: scrollX + cardWidth + 8,
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  const handlePreviousClick = () => {
    onEvent(ANALYTICS_CLICK_EVENTS.ON_CAROUSEL_BACK_CLICK, analyticsMetadata)

    if (ref.current && cardWidth) {
      // Scroll by one card width + gap
      ref.current.scrollTo({
        left: Math.max(0, scrollX - (cardWidth + 8)),
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  const handleScroll = () => {
    if (ref.current) {
      setScrollX(ref.current.scrollLeft)
    }
  }

  // *************** Mobile handlers ************************
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd

    // swiping left
    if (distance > MINIMUM_SWIPE_DISTANCE) {
      onEvent(ANALYTICS_CLICK_EVENTS.ON_CAROUSEL_FORWARD_SWIPE, analyticsMetadata)
    } else {
      // swiping right
      onEvent(ANALYTICS_CLICK_EVENTS.ON_CAROUSEL_BACK_SWIPE, analyticsMetadata)
    }
  }

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchStart = (e: React.TouchEvent) => {
    // otherwise the swipe is fired even with usual touch events
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  // Calculate active card index based on scroll position and card width
  const activeCardIndex = cardWidth ? Math.round(scrollX / (cardWidth + 8)) : 0
  const scrollBoundry = ref.current ? ref.current.scrollWidth - ref.current.offsetWidth : 0

  return (
    <Stack
      aria-label={copy?.carousel_title}
      aria-roledescription="carousel"
      ref={carouselRef}
      role="region"
      sx={{
        alignItems: 'center',
        boxSizing: 'border-box',
        height: heightOverrides,
        width: widthOverrides ? widthOverrides : '100%',
      }}
    >
      <Stack
        direction="row"
        onScroll={handleScroll}
        onTouchEnd={onTouchEnd}
        onTouchMove={onTouchMove}
        onTouchStart={onTouchStart}
        ref={ref}
        sx={{
          alignItems: 'center',
          height: heightOverrides && '100%',
          gap: 8,
          listStyleType: 'none',
          m: 0,
          maxWidth: showWithMargin ? `calc(100% - 32px)` : '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          p: 0,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <BeatList
          activeCardIndex={activeCardIndex}
          beats={beats}
          cardWidth={cardWidth}
          elevation={elevation}
          heightOverrides={heightOverrides}
          onCardClick={onCardClick}
          posthog={posthog}
          showIcon={showIcon}
          variant={variant}
        />
      </Stack>
      {showCarouselControls && (
        <CarouselControls
          currentBeatIndex={activeCardIndex + visibleCardsCount - 1}
          numBeats={beats.length + 1}
          onNextClick={handleNextClick}
          onPreviousClick={handlePreviousClick}
          scrollRightBoundary={scrollBoundry}
          scrollX={scrollX}
          shouldApplyDotAnimation={allowSingleCardPeeking}
          visibleCardsCount={visibleCardsCount}
        />
      )}
    </Stack>
  )
}

export default observer(MicroBeatCarousel)
